import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Modal } from '~/core/components/Modal';
import { Button } from '~/core/components/Button';
import { useToast } from './ToastContext';
import { useAccount } from 'wagmi';
import { useAtomValue } from 'jotai';
import { atomWalletModal } from '~/blinko/modals/wallet.modal';
import { createPortal } from 'react-dom';
import { ContainerShape, HexagonShape, InnerShape } from '~/blinko/components/Shape';
import { icons } from '~/blinko/assets/icons/icons';

interface ErrorItem {
  id: number;
  title: string;
  error: any;
  additionalInfo?: string;
}

interface ErrorContextProps {
  handleError: (title: string, error: any, additionalInfo?: string) => void
  renderError: (title: string, error: Error) => JSX.Element
}

const ErrorContext = createContext<ErrorContextProps | undefined>(undefined);

export const ErrorProvider = ({ children }: { children: ReactNode }) => {
  const [errors, setErrors] = useState<ErrorItem[]>([]);

  const addError = (title: string, error: any, additionalInfo?: string) => {
    const id = Date.now() + Math.random();
    setErrors((prev) => [...prev, { id, title, error, additionalInfo }]);
  };

  const removeError = (id: number) => {
    setErrors((prev) => prev.filter((error) => error.id !== id));
  };

  const handleError = (title: string, error: any, additionalInfo?: string) => addError(title, error, additionalInfo)
  const renderError = (title: string, error: Error) => <ErrorBoundary title={title} error={error} />

  const value = { handleError, renderError }

  return (
    <ErrorContext.Provider value={value}>
      {children}
      {errors.map((error) => (
        <ErrorItem key={error.id} error={error} removeError={removeError} />
      ))}
    </ErrorContext.Provider>
  );
};

const ErrorItem = ({ error, removeError }: { error: ErrorItem, removeError: (id: number) => void }) => {
  const [visible, setVisible] = useState(true)
  const toast = useToast()

  const isSigned = useAtomValue(atomWalletModal.isSigned)
  const { address, connector } = useAccount()

  useEffect(() => {
    if (parsedError.includes('User rejected request') || parsedError.includes('User rejected the request') || parsedError.includes('The user rejected the request')) {
      toast.info('Transaction cancelled')
      onDismiss()
    } else if (parsedError.includes('insufficient funds for gas + value')) {
      toast.error('Insufficient funds, you need more ETH to cover this')
      onDismiss()
    } else if (parsedError.includes('PopupBlocked') || error.error?.message === 'PopupBlocked') {
      toast.error('Popup blocked. Please allow popups for this site and try again.')
      onDismiss()
    } else {
      // This repo is the clean-room proof of the game economy handed to the frontend team —
      // no external telemetry. Errors go to the console, where QA can actually see them.
      console.error(error.title, error.error, {
        title: error.title,
        additionalInfo: error.additionalInfo,
        address,
        isSigned,
        connector,
      })
    }
  }, [error])

  const onCopy = () => {
    const copyText = `Account: ${address}\nIsSigned: ${isSigned}\nConnector: (${connector?.id})\nBrowser: ${navigator.userAgent}\nPath: ${window.location.pathname}\nAdditional Info: ${error.additionalInfo}\n\nError:\n${parsedError}`;
    navigator.clipboard.writeText(copyText);
    toast.success('Error copied. Please share it if you need help.');
  }

  const onCancel = () => {
    setVisible(false)
  }

  const onDismiss = () => {
    removeError(error.id)
  }

  const parsedError = (() => {
    if (error.error instanceof Error) {
      const errorName = error.error.constructor.name;
      return `${errorName}: ${error.error.message}\nStack: ${error.error.stack}`;
    } else if (typeof error.error === 'string') {
      return error.error;
    } else if (typeof error.error === 'object' && error.error !== null) {
      return `Error: ${JSON.stringify(error.error)}`;
    } else {
      return `${error.error}`;
    }
  })();

  return visible && createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#18181B]/50">
      <div>
        <ContainerShape {...{cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 }}>
          <div className="p-6 w-[350px] md:w-[448px]">
            <div className="flex flex-row justify-between">
              <div className="mt-1 text-2xl font-blinko font-black text-white">
                {error.title}
              </div>

              <button className="" onClick={onCancel}>
                <HexagonShape cornerRadius={6} strokeWidth={1}>
                  <div className="flex justify-center items-center p-2">
                    <img src={icons.close} className='w-6 h-6' />
                  </div>
                </HexagonShape>
              </button>
              
            </div>

            <div className="mt-3">
              <div
                className="p-4 rounded-lg bg-white/5 h-[172px] overflow-scroll whitespace-pre overscroll-contain"
              >
                <p className="text-sm text-white">{parsedError}</p>
              </div>
            </div>
        </div>
        
        </ContainerShape>
      </div>
    </div>
  , document.body)
};

const ErrorBoundary = ({ title, error }: { title: string, error: Error }) => {
  const [visible, setVisible] = useState(false)

  const parsedError = (() => {
    const errorName = error.constructor.name;
    return `${errorName}: ${error.message}\nStack: ${error.stack}`;
  })();

  return (
    <div className='flex flex-col gap-2 px-6'>
      <div className='flex items-center gap-2'>
        <p className="text-lg text-[#191d29]">
          {title}
        </p>
        <button className='text-base' onClick={() => setVisible(x => !x)}>
          {!visible ? '(show error)' : '(hide error)'}
        </button>
      </div>
      {visible && (
        <div className='p-4 bg-slate-100 rounded'>
          <p className="text-lg text-[#191d29]">
            {parsedError}
          </p>
        </div>
      )}
    </div>
  )
}

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within a ErrorProvider');
  }
  return context;
};
